import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { buildMapsNavigationUrl } from "../lib/maps";
import Navbar from "../components/Navbar";
import RestaurantMap from "../components/RestaurantMap";
import { localizeSpecial, translateCategory, translateDayCodes, useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { MapPin, Search, Compass, Navigation, Filter, Loader2, ChevronLeft, ChevronRight, Utensils, Clock, Tag, ExternalLink, X, ChevronsUpDown } from "lucide-react";
import { toast, Toaster } from "sonner";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/151c05f6-6397-4c88-bc98-7844b83fd5de/images/5e89c1aa56905df5468661e90f850540c5411444ae85fc918660f4267965b176.png";
const PLACEHOLDER = "/images/lunchspot-bistro-placeholder.png";

const WEEKDAY_CODES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_SWIPE_THRESHOLD_PX = 72;
const DAY_SWIPE_VERTICAL_TOLERANCE_PX = 48;
const FALLBACK_STICKY_SCROLL_OFFSET_PX = 140;
const STICKY_SCROLL_GAP_PX = 12;
const RESTAURANT_SCROLL_SETTLE_DELAY_MS = 280;

function getStickyScrollOffset() {
  if (typeof document === "undefined") return FALLBACK_STICKY_SCROLL_OFFSET_PX;

  const stickyElements = [
    document.querySelector(".glass-nav"),
    document.querySelector('[data-testid="day-strip-shell"]'),
  ];

  const stickyBottom = stickyElements.reduce((bottom, element) => {
    if (!element?.getBoundingClientRect) return bottom;
    const rect = element.getBoundingClientRect();
    return Math.max(bottom, Number.isFinite(rect.bottom) ? rect.bottom : 0);
  }, 0);

  return stickyBottom > 0
    ? Math.ceil(stickyBottom + STICKY_SCROLL_GAP_PX)
    : FALLBACK_STICKY_SCROLL_OFFSET_PX;
}

function scrollElementBelowSticky(element, behavior = "smooth") {
  if (!element) return;
  const offset = getStickyScrollOffset();
  const elementPosition = element.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({
    top: Math.max(0, elementPosition - offset),
    behavior,
  });
}

function getScrollTargetElement(element) {
  return element?.querySelector?.('[data-testid^="restaurant-trigger-"]') || element;
}

function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isAvailableOn(special, date) {
  // 1. Date-range check: the selected calendar date must lie within valid_from..valid_until (if set)
  const iso = toIsoDate(date);
  if (special.valid_from && iso < special.valid_from) return false;
  if (special.valid_until && iso > special.valid_until) return false;
  // 2. Weekday check
  const dayCode = WEEKDAY_CODES[date.getDay()];
  const days = special.valid_days || [];
  if (days.length === 0) return true; // unknown weekday availability → assume available
  if (days.includes("Daily")) return true;
  return days.includes(dayCode);
}

function isLocalhost(hostname) {
  return ["localhost", "127.0.0.1", "::1"].includes(hostname);
}

const LOCATION_STORAGE_KEY = "ls_location";

// Read the last granted location from localStorage. Returns { lat, lng } or null.
function readStoredLocation() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.lat === "number" &&
      typeof parsed.lng === "number" &&
      Number.isFinite(parsed.lat) &&
      Number.isFinite(parsed.lng)
    ) {
      return { lat: parsed.lat, lng: parsed.lng };
    }
  } catch {
    // Corrupt/blocked storage: ignore and fall back to no stored location.
  }
  return null;
}

function writeStoredLocation(location) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    if (location) {
      window.localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
    } else {
      window.localStorage.removeItem(LOCATION_STORAGE_KEY);
    }
  } catch {
    // Storage may be unavailable (private mode/quota); persistence is best-effort.
  }
}

function getGeolocationUnavailableMessage(t) {
  if (typeof window !== "undefined" && !window.isSecureContext && !isLocalhost(window.location.hostname)) {
    return t.locationRequiresHttps;
  }
  return t.geolocationUnsupported;
}

function RestaurantNavigationLink({ restaurant, testId, t }) {
  const navigationUrl = buildMapsNavigationUrl(restaurant);
  if (!navigationUrl) return null;

  return (
    <a
      href={navigationUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t.navigateToRestaurant(restaurant.restaurant_name)}
      title={t.navigateToRestaurant(restaurant.restaurant_name)}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#3D4035] text-[#F7F5F0] shadow-sm shadow-[#3D4035]/10 transition hover:-translate-y-0.5 hover:bg-[#2c2f25] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E07A5F]/40"
      data-testid={testId}
    >
      <Navigation size={14} strokeWidth={2.4} aria-hidden="true" />
    </a>
  );
}

export default function FeedPage() {
  const { language, t } = useLanguage();
  const [location, setLocation] = useState(() => readStoredLocation());
  const [locating, setLocating] = useState(false);
  const [rawSpecials, setRawSpecials] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchRequestIdRef = useRef(0);
  const translationRef = useRef(t);
  const [query, setQuery] = useState("");
  const [radius, setRadius] = useState(2);
  const [maxPrice, setMaxPrice] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [dayOffset, setDayOffset] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const daySwipeStartRef = useRef(null);
  const dayTabRefs = useRef({});
  const shouldAlignSelectedDayRef = useRef(false);
  
  // Track which restaurant is currently "active" (last opened)
  const [activeRestaurant, setActiveRestaurant] = useState(null);
  // Refs for scrolling to restaurant elements
  const restaurantRefs = useRef({});

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  const specials = useMemo(
    () => {
      const list = Array.isArray(rawSpecials) ? rawSpecials : [];
      return list.map((sp) => localizeSpecial(sp, language));
    },
    [rawSpecials, language]
  );

  const scrollToRestaurant = useCallback((restaurantKey, delay = 50) => {
    const align = (behavior = "smooth") => {
      const element = restaurantRefs.current[restaurantKey];
      scrollElementBelowSticky(getScrollTargetElement(element), behavior);
    };

    setTimeout(() => align("smooth"), delay);
    // Mobile standalone/PWA viewports can change height while the accordion opens
    // and Radix content finishes layout. Re-measure once after layout settles so
    // the restaurant title lands at the same visual offset on every screen height.
    setTimeout(() => align("auto"), delay + RESTAURANT_SCROLL_SETTLE_DELAY_MS);
  }, []);

  const showRestaurantDetails = useCallback((restaurantKey) => {
    setViewMode("list");
    setActiveRestaurant(restaurantKey);
    setExpanded((current) => (
      current.includes(restaurantKey) ? current : [...current, restaurantKey]
    ));
    scrollToRestaurant(restaurantKey);
  }, [scrollToRestaurant]);

  // Build the 7-day strip starting from today
  const dayStrip = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      return {
        offset: i,
        date: d,
        code: WEEKDAY_CODES[d.getDay()],
        long: t.weekdaysLong[d.getDay()],
        dayNum: d.getDate(),
        label: i === 0 ? t.today : t.weekdaysShort[d.getDay()],
      };
    });
  }, [t]);

  const selectedDay = dayStrip[dayOffset];

  const changeDayOffset = useCallback((nextOffset, { alignSelected = false } = {}) => {
    setDayOffset((current) => {
      const requested = typeof nextOffset === "function" ? nextOffset(current) : nextOffset;
      const clamped = Math.min(6, Math.max(0, requested));
      if (alignSelected && clamped !== current) {
        shouldAlignSelectedDayRef.current = true;
      }
      return clamped;
    });
  }, []);

  const handleDayTouchStart = useCallback((event) => {
    if (event.target?.closest?.('[data-testid="restaurant-map"]')) {
      daySwipeStartRef.current = null;
      return;
    }
    const touch = event.touches?.[0] || event.changedTouches?.[0];
    if (!touch) return;
    daySwipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleDayTouchEnd = useCallback((event) => {
    if (event.target?.closest?.('[data-testid="restaurant-map"]')) {
      daySwipeStartRef.current = null;
      return;
    }
    const start = daySwipeStartRef.current;
    daySwipeStartRef.current = null;
    const touch = event.changedTouches?.[0];
    if (!start || !touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < DAY_SWIPE_THRESHOLD_PX) return;
    if (Math.abs(deltaY) > DAY_SWIPE_VERTICAL_TOLERANCE_PX) return;

    changeDayOffset((current) => current + (deltaX < 0 ? 1 : -1), { alignSelected: true });
  }, [changeDayOffset]);

  useEffect(() => {
    if (!shouldAlignSelectedDayRef.current) return;
    shouldAlignSelectedDayRef.current = false;
    dayTabRefs.current[dayOffset]?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  }, [dayOffset]);

  const fetchSpecials = useCallback(async () => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;
    setLoading(true);
    try {
      const params = { radius_km: radius };
      if (location) { params.lat = location.lat; params.lng = location.lng; }
      const r = await api.get("/specials", { params });
      if (requestId === fetchRequestIdRef.current) setRawSpecials(r.data);
    } catch {
      if (requestId === fetchRequestIdRef.current) toast.error(translationRef.current.failedToLoadSpecials);
    } finally {
      if (requestId === fetchRequestIdRef.current) setLoading(false);
    }
  }, [location, radius]);

  const clearLocation = useCallback(() => {
    setLocation(null);
    writeStoredLocation(null);
  }, []);

  const resetFilters = useCallback(() => {
    setQuery("");
    setMaxPrice("");
    setSelectedCategory(null);
    setSelectedCuisine(null);
  }, []);

  // Request the browser location. When `silent` is true (used for the automatic
  // refresh on load when permission is already granted), no toasts are shown and no
  // busy spinner is toggled, so it stays invisible to the user.
  const requestLocation = useCallback(({ silent = false } = {}) => {
    const currentT = translationRef.current;
    if (!navigator.geolocation) {
      if (!silent) toast.error(getGeolocationUnavailableMessage(currentT));
      return;
    }
    if (typeof window !== "undefined" && !window.isSecureContext && !isLocalhost(window.location.hostname)) {
      if (!silent) toast.error(getGeolocationUnavailableMessage(currentT));
      return;
    }
    if (!silent) setLocating(true);
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setLocation(next);
          writeStoredLocation(next);
          if (!silent) {
            setLocating(false);
            toast.success(translationRef.current.locationDetected);
          }
        },
        (err) => {
          if (!silent) {
            setLocating(false);
            const latestT = translationRef.current;
            const message = err?.message ? latestT.locationUnavailableWithReason(err.message) : latestT.locationUnavailable;
            toast.error(message);
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    } catch (err) {
      if (!silent) {
        setLocating(false);
        const latestT = translationRef.current;
        toast.error(err?.message ? latestT.locationUnavailableWithReason(err.message) : getGeolocationUnavailableMessage(latestT));
      }
    }
  }, []);

  useEffect(() => { fetchSpecials(); }, [fetchSpecials]);

  // Remember an earlier permission grant: if the browser reports geolocation
  // permission is already "granted", silently refresh the location on load so the
  // user does not have to click "Use my location" again.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) return;
    let cancelled = false;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (!cancelled && status.state === "granted") {
          requestLocation({ silent: true });
        }
      })
      .catch(() => {
        // Permissions API unavailable or query rejected: rely on cached location only.
      });
    return () => { cancelled = true; };
  }, [requestLocation]);

  // Handle accordion expansion changes - track active restaurant and scroll
  const handleAccordionChange = useCallback((newExpanded) => {
    // Find newly opened restaurant (in newExpanded but not in current expanded)
    const newlyOpened = newExpanded.find((id) => !expanded.includes(id));
    
    if (newlyOpened) {
      setActiveRestaurant(newlyOpened);
      setExpanded(newExpanded);
      scrollToRestaurant(newlyOpened);
    } else {
      // Restaurant was closed
      const closedRestaurant = expanded.find((id) => !newExpanded.includes(id));
      // If the active restaurant was closed, clear it
      if (closedRestaurant === activeRestaurant) {
        setActiveRestaurant(null);
      }
      setExpanded(newExpanded);
    }
  }, [expanded, activeRestaurant, scrollToRestaurant]);

  // Available categories & cuisines from loaded specials
  const availableCategories = useMemo(() => {
    const set = new Set();
    for (const sp of specials) {
      if (sp.category) set.add(sp.category);
    }
    return Array.from(set);
  }, [specials]);

  const availableCuisines = useMemo(() => {
    const set = new Set();
    for (const sp of specials) {
      if (sp.cuisine) set.add(sp.cuisine);
    }
    return Array.from(set);
  }, [specials]);

  // Client-side filter by query, maxPrice, category, and cuisine
  const filteredSpecials = useMemo(() => {
    const q = query.trim().toLowerCase();
    const parsedMaxPrice = maxPrice !== "" && maxPrice !== null ? Number(maxPrice) : null;
    const hasMaxPrice = parsedMaxPrice !== null && Number.isFinite(parsedMaxPrice) && parsedMaxPrice >= 0;

    return specials.filter((sp) => {
      if (q) {
        const haystack = [
          sp.display_name,
          sp.name,
          sp.display_description,
          sp.description,
          sp.restaurant_name,
          sp.cuisine,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (hasMaxPrice) {
        if (sp.price == null || !Number.isFinite(Number(sp.price)) || Number(sp.price) > parsedMaxPrice) {
          return false;
        }
      }
      if (selectedCategory && sp.category !== selectedCategory) return false;
      if (selectedCuisine && sp.cuisine !== selectedCuisine) return false;
      return true;
    });
  }, [specials, query, maxPrice, selectedCategory, selectedCuisine]);

  const hasActiveFilters = Boolean(
    query.trim() || (maxPrice !== "" && maxPrice !== null) || selectedCategory || selectedCuisine
  );

  // Per-day unique restaurant counts across the 7-day strip
  const dayRestaurantCounts = useMemo(() => {
    const counts = {};
    for (const d of dayStrip) {
      const uniqueRestaurants = new Set();
      for (const sp of filteredSpecials) {
        if (isAvailableOn(sp, d.date)) {
          uniqueRestaurants.add(sp.source_id || sp.restaurant_name);
        }
      }
      counts[d.offset] = uniqueRestaurants.size;
    }
    return counts;
  }, [dayStrip, filteredSpecials]);

  // Filter by selected day + group by restaurant
  const restaurantGroups = useMemo(() => {
    const filtered = filteredSpecials.filter((sp) => isAvailableOn(sp, selectedDay.date));
    const map = new Map();
    for (const sp of filtered) {
      const key = sp.source_id || sp.restaurant_name;
      if (!map.has(key)) {
        map.set(key, {
          source_id: sp.source_id,
          restaurant_name: sp.restaurant_name,
          address: sp.address,
          lat: sp.lat,
          lng: sp.lng,
          cuisine: sp.cuisine,
          image_url: sp.image_url,
          source_url: sp.source_url,
          opening_hours: sp.opening_hours,
          distance_km: sp.distance_km,
          specials: [],
        });
      }
      map.get(key).specials.push(sp);
    }
    const groups = Array.from(map.values()).map((group) => {
      const prices = group.specials
        .map((sp) => (sp.price != null ? Number(sp.price) : NaN))
        .filter((p) => Number.isFinite(p));
      const minPrice = prices.length > 0 ? Math.min(...prices) : null;
      const maxPriceVal = prices.length > 0 ? Math.max(...prices) : null;
      const priceRangeLabel =
        minPrice != null && maxPriceVal != null
          ? minPrice === maxPriceVal
            ? `${minPrice.toFixed(2)} €`
            : `${minPrice.toFixed(2)} € – ${maxPriceVal.toFixed(2)} €`
          : null;
      return {
        ...group,
        minPrice,
        maxPrice: maxPriceVal,
        priceRangeLabel,
      };
    });
    // sort by distance (nulls last)
    groups.sort((a, b) => {
      const da = a.distance_km ?? Infinity;
      const db = b.distance_km ?? Infinity;
      return da - db;
    });
    return groups;
  }, [filteredSpecials, selectedDay]);

  const allExpanded = useMemo(
    () =>
      restaurantGroups.length > 0 &&
      restaurantGroups.every((g) => expanded.includes(g.source_id || g.restaurant_name)),
    [restaurantGroups, expanded]
  );

  const toggleExpandAll = useCallback(() => {
    if (allExpanded) {
      setExpanded([]);
      setActiveRestaurant(null);
    } else {
      setExpanded(restaurantGroups.map((g) => g.source_id || g.restaurant_name));
    }
  }, [allExpanded, restaurantGroups]);

  const totalAvailable = useMemo(
    () => restaurantGroups.reduce((acc, g) => acc + g.specials.length, 0),
    [restaurantGroups]
  );

  // When day changes, keep active restaurant open if it's available on the new day
  // and scroll to it
  useEffect(() => {
    if (!activeRestaurant) return;
    
    // Check if active restaurant exists in current day's groups
    const restaurantKey = activeRestaurant;
    const isAvailable = restaurantGroups.some(
      (g) => (g.source_id || g.restaurant_name) === restaurantKey
    );
    
    if (isAvailable) {
      // Keep it expanded
      if (!expanded.includes(restaurantKey)) {
        setExpanded((prev) => [...prev.filter((id) => id !== restaurantKey), restaurantKey]);
      }
      scrollToRestaurant(restaurantKey, 100);
    } else {
      // Restaurant not available on this day, clear active state
      setActiveRestaurant(null);
      setExpanded((prev) => prev.filter((id) => id !== restaurantKey));
    }
  }, [dayOffset, restaurantGroups, activeRestaurant, scrollToRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Toaster position="top-right" richColors />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-90" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tighter leading-tight text-[#3D4035] text-balance">
              {t.heroTitleStart} <span className="italic text-[#E07A5F]">{t.heroTitleHighlight}</span> {t.heroTitleEnd}
            </h1>
            <div className="flex flex-wrap gap-2 items-center shrink-0">
              <Button
                onClick={() => requestLocation()}
                disabled={locating}
                size="sm"
                className="rounded-full bg-[#E07A5F] hover:bg-[#cf6b51] text-white font-medium shadow-sm px-4 h-9"
                data-testid="locate-me-btn"
              >
                {locating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Compass className="mr-2 h-4 w-4" />}
                {location ? t.updateLocation : t.useMyLocation}
              </Button>
              {location && (
                <button
                  type="button"
                  onClick={clearLocation}
                  aria-label={t.clearLocation}
                  title={t.clearLocation}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm border border-[rgba(61,64,53,0.08)] text-[#6A6F5C] hover:bg-[#EFEBE1] hover:text-[#3D4035] transition-colors"
                  data-testid="clear-location-btn"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Collapsible filters */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-6" data-testid="filters-shell">
        <div className="bg-white rounded-2xl border border-[rgba(61,64,53,0.08)] shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="feed-filters-content"
            data-testid="filters-toggle"
            className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left"
          >
            <span className="inline-flex items-center gap-2 font-heading text-lg font-semibold text-[#3D4035]">
              <Filter size={16} className="text-[#E07A5F]" />
              {t.filter}
            </span>
            <span className="text-xs uppercase tracking-[0.16em] font-bold text-[#6A6F5C]">
              {filtersOpen ? t.collapse : t.filterSummary}
            </span>
          </button>
          <div
            id="feed-filters-content"
            hidden={!filtersOpen}
            data-testid="filters-content"
            className={`${filtersOpen ? "flex" : "hidden"} border-t border-[rgba(61,64,53,0.08)] p-4 flex-wrap items-end gap-4`}
          >
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6A6F5C] mb-1.5 block">{t.search}</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9E8D]" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t.searchPlaceholder}
                  className="pl-9 rounded-full bg-[#F7F5F0] border-[rgba(61,64,53,0.08)] h-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="min-w-[200px]">
              <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6A6F5C] mb-1.5 block">
                {t.radius}: {radius} km
              </label>
              <Slider value={[radius]} onValueChange={(v) => setRadius(v[0])} min={1} max={50} step={1} data-testid="radius-slider" />
            </div>
            <div className="w-32">
              <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6A6F5C] mb-1.5 block">{t.maxPrice}</label>
              <Input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="—" className="rounded-full bg-[#F7F5F0] border-[rgba(61,64,53,0.08)] h-10" data-testid="max-price-input" />
            </div>
            <Button onClick={fetchSpecials} className="rounded-full bg-[#3D4035] hover:bg-[#2c2f25] text-white h-10" data-testid="apply-filters-btn">
              <Filter size={14} className="mr-1.5" /> {t.apply}
            </Button>
            {(availableCategories.length > 0 || availableCuisines.length > 0 || hasActiveFilters) && (
              <div className="w-full pt-3 border-t border-[rgba(61,64,53,0.06)] flex flex-wrap items-center gap-2" data-testid="quick-filter-chips">
                {availableCategories.map((cat) => {
                  const active = selectedCategory === cat;
                  return (
                    <button
                      key={`cat-${cat}`}
                      type="button"
                      onClick={() => setSelectedCategory((cur) => (cur === cat ? null : cat))}
                      aria-pressed={active}
                      data-testid={`chip-category-${cat}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
                        active
                          ? "bg-[#3D4035] text-[#F7F5F0] border-[#3D4035]"
                          : "bg-[#F7F5F0] text-[#3D4035] border-[rgba(61,64,53,0.12)] hover:bg-[#EFEBE1]"
                      }`}
                    >
                      {translateCategory(cat, t)}
                    </button>
                  );
                })}
                {availableCuisines.map((cuisine) => {
                  const active = selectedCuisine === cuisine;
                  return (
                    <button
                      key={`cuisine-${cuisine}`}
                      type="button"
                      onClick={() => setSelectedCuisine((cur) => (cur === cuisine ? null : cuisine))}
                      aria-pressed={active}
                      data-testid={`chip-cuisine-${cuisine}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
                        active
                          ? "bg-[#E07A5F] text-white border-[#E07A5F]"
                          : "bg-[#F7F5F0] text-[#6A6F5C] border-[rgba(61,64,53,0.12)] hover:bg-[#EFEBE1]"
                      }`}
                    >
                      <Tag size={11} aria-hidden="true" />
                      {cuisine}
                    </button>
                  );
                })}
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    data-testid="reset-filters-btn"
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-[#E07A5F] bg-white border border-[#E07A5F]/30 hover:bg-[#E07A5F]/10 transition-colors"
                  >
                    <X size={12} aria-hidden="true" />
                    {t.resetFilters}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Day strip */}
      <section
        className="sticky top-16 z-30 bg-[#F7F5F0]/95 backdrop-blur-md border-b border-[rgba(61,64,53,0.06)]"
        data-testid="day-strip-shell"
        onTouchStart={handleDayTouchStart}
        onTouchEnd={handleDayTouchEnd}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-3 pb-3">
          <div className="bg-white border border-[rgba(61,64,53,0.08)] rounded-2xl p-3 flex items-center gap-2 shadow-sm">
            <Button
              variant="ghost" size="icon"
              onClick={() => changeDayOffset((current) => current - 1)}
              disabled={dayOffset === 0}
              data-testid="day-prev-btn"
              className="shrink-0"
            >
              <ChevronLeft size={18} />
            </Button>
            <div className="flex-1 overflow-x-auto no-scrollbar">
              <div className="flex gap-2 min-w-max">
                {dayStrip.map((d) => {
                  const active = d.offset === dayOffset;
                  const count = dayRestaurantCounts[d.offset] ?? 0;
                  return (
                    <button
                      key={d.offset}
                      ref={(el) => { dayTabRefs.current[d.offset] = el; }}
                      onClick={() => changeDayOffset(d.offset)}
                      data-testid={`day-tab-${d.offset}`}
                      className={`flex flex-col items-center px-4 py-2 rounded-xl min-w-[72px] transition-all ${
                        active
                          ? "bg-[#3D4035] text-[#F7F5F0]"
                          : count === 0
                            ? "bg-[#F7F5F0] text-[#9A9E8D] opacity-60 hover:bg-[#EFEBE1]"
                            : "bg-[#F7F5F0] text-[#3D4035] hover:bg-[#EFEBE1]"
                      }`}
                    >
                      <span className={`text-[10px] uppercase tracking-[0.16em] font-bold ${active ? "text-[#E07A5F]" : "text-[#9A9E8D]"}`}>
                        {d.offset === 0 ? t.today : d.label}
                      </span>
                      <span className="font-heading text-xl font-semibold leading-tight mt-0.5">{d.dayNum}</span>
                      <span
                        data-testid={`day-tab-count-${d.offset}`}
                        className={`mt-1 inline-flex items-center justify-center rounded-full px-1.5 py-0.2 text-[10px] font-semibold ${
                          active
                            ? "bg-[#E07A5F] text-white"
                            : count > 0
                              ? "bg-[#EFEBE1] text-[#3D4035]"
                              : "bg-transparent text-[#9A9E8D]"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <Button
              variant="ghost" size="icon"
              onClick={() => changeDayOffset((current) => current + 1)}
              disabled={dayOffset === 6}
              data-testid="day-next-btn"
              className="shrink-0"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* Grouped restaurants */}
      <section
        className="max-w-7xl mx-auto px-5 sm:px-8 pt-6 pb-20"
        data-testid="feed-swipe-zone"
        onTouchStart={handleDayTouchStart}
        onTouchEnd={handleDayTouchEnd}
      >
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between" data-testid="feed-results-header">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight whitespace-nowrap">
            {dayOffset === 0 ? t.todaysLunch : t.dayLunch(selectedDay.long)}
          </h2>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-sm text-[#6A6F5C] sm:justify-end" data-testid="feed-results-actions">
            <div className="flex items-center gap-2 whitespace-nowrap" data-testid="feed-results-counts">
              <span data-testid="restaurants-count">{t.restaurants(restaurantGroups.length)}</span>
              <span className="text-[#9A9E8D]">·</span>
              <span data-testid="specials-count">{t.dishes(totalAvailable)}</span>
            </div>
            <div className="inline-flex shrink-0 rounded-full border border-[rgba(61,64,53,0.08)] bg-white p-1 shadow-sm" aria-label="Feed view">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                data-testid="feed-view-list-btn"
                className={`rounded-full px-3 py-1 text-xs ${viewMode === "list" ? "bg-[#3D4035] text-[#F7F5F0]" : "text-[#3D4035]"}`}
              >
                {t.list}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setViewMode("map")}
                aria-pressed={viewMode === "map"}
                data-testid="feed-view-map-btn"
                className={`rounded-full px-3 py-1 text-xs ${viewMode === "map" ? "bg-[#3D4035] text-[#F7F5F0]" : "text-[#3D4035]"}`}
              >
                {t.map}
              </Button>
            </div>
            {restaurantGroups.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={toggleExpandAll}
                disabled={viewMode !== "list"}
                aria-hidden={viewMode !== "list"}
                data-testid="expand-all-btn"
                className={`rounded-full border border-[rgba(61,64,53,0.08)] bg-white px-3 py-1 text-xs text-[#3D4035] hover:bg-[#EFEBE1] shadow-sm ${
                  viewMode !== "list" ? "hidden sm:inline-flex sm:invisible sm:pointer-events-none" : ""
                }`}
              >
                <ChevronsUpDown size={13} className="mr-1" aria-hidden="true" />
                {allExpanded ? t.collapseAll : t.expandAll}
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#6A6F5C]">
            <Loader2 className="animate-spin mr-2" /> {t.loading}
          </div>
        ) : restaurantGroups.length === 0 ? (
          <div className="bg-white border border-dashed border-[rgba(61,64,53,0.15)] rounded-2xl p-12 text-center" data-testid="empty-state">
            <Utensils className="mx-auto mb-3 text-[#9A9E8D]" size={28} />
            <p className="font-heading text-xl font-semibold mb-2 text-[#3D4035]">
              {t.noLunchForDay(selectedDay.long)}
            </p>
            <p className="text-[#6A6F5C]">{t.emptyHint}</p>
          </div>
        ) : viewMode === "map" ? (
          <RestaurantMap
            restaurants={restaurantGroups}
            userLocation={location}
            radiusKm={radius}
            activeRestaurant={activeRestaurant}
            onShowDetails={showRestaurantDetails}
          />
        ) : (
          <Accordion
            type="multiple"
            value={expanded}
            onValueChange={handleAccordionChange}
            className="space-y-3"
            style={{ overflowAnchor: "none" }}
            data-testid="restaurants-accordion"
          >
            <AnimatePresence>
              {restaurantGroups.map((g, idx) => {
                const key = g.source_id || g.restaurant_name;
                return (
                <motion.div
                  key={key}
                  ref={(el) => { restaurantRefs.current[key] = el; }}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: idx * 0.03, ease: [0.22, 1, 0.36, 1] }}
                >
                  <AccordionItem
                    value={key}
                    className="border border-[rgba(61,64,53,0.08)] bg-white rounded-2xl overflow-hidden data-[state=open]:shadow-md transition-shadow"
                    data-testid={`restaurant-${key}`}
                  >
                    <AccordionTrigger
                      className="px-5 sm:px-6 py-4 hover:no-underline hover:bg-[#FBFAF6]"
                      data-testid={`restaurant-trigger-${key}`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0 text-left">
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#EFEBE1] shrink-0">
                          <img
                            src={g.image_url || PLACEHOLDER}
                            alt={g.restaurant_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <h3 className="font-heading text-lg sm:text-xl font-semibold leading-tight truncate">
                              {g.restaurant_name}
                            </h3>
                            <Badge className="bg-[#E07A5F] text-white text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5">
                              {t.dishes(g.specials.length)}
                            </Badge>
                            {g.priceRangeLabel && (
                              <span
                                className="inline-flex items-center rounded-full bg-[#F7F5F0] px-2.5 py-0.5 text-xs font-medium text-[#3D4035]"
                                data-testid={`restaurant-price-range-${key}`}
                              >
                                {g.priceRangeLabel}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-[#6A6F5C] mt-1">
                            {g.cuisine && <span className="inline-flex items-center gap-1"><Tag size={11} /> {g.cuisine}</span>}
                            {g.distance_km != null && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {g.distance_km.toFixed(1)} km</span>}
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    {g.address && (
                      <div
                        className="flex items-center gap-2 border-t border-[rgba(61,64,53,0.06)] px-5 py-2.5 text-xs text-[#6A6F5C] sm:px-6"
                        data-testid={`restaurant-address-row-${g.source_id || g.restaurant_name}`}
                      >
                        <MapPin size={12} className="shrink-0 text-[#9A9E8D]" aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate">{g.address}</span>
                        <RestaurantNavigationLink
                          restaurant={g}
                          testId={`restaurant-navigation-${g.source_id || g.restaurant_name}`}
                          t={t}
                        />
                      </div>
                    )}
                    {(g.source_url || g.opening_hours) && (
                      <div
                        className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[rgba(61,64,53,0.06)] px-5 py-2.5 text-xs text-[#6A6F5C] sm:px-6"
                        data-testid={`restaurant-meta-${g.source_id || g.restaurant_name}`}
                      >
                        {g.opening_hours && (
                          <span className="inline-flex min-w-0 items-center gap-1.5">
                            <Clock size={12} className="shrink-0 text-[#9A9E8D]" aria-hidden="true" />
                            <span className="truncate">{g.opening_hours}</span>
                          </span>
                        )}
                        {g.source_url && (
                          <a
                            href={g.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 font-semibold text-[#E07A5F] hover:underline"
                            data-testid={`restaurant-website-${g.source_id || g.restaurant_name}`}
                          >
                            {t.website}
                            <ExternalLink size={12} aria-hidden="true" />
                          </a>
                        )}
                      </div>
                    )}
                    <AccordionContent className="px-5 sm:px-6 pb-5 pt-1">
                      <ul className="divide-y divide-[rgba(61,64,53,0.06)]">
                        {g.specials.map((sp) => (
                          <li key={sp.id} className="py-4 flex gap-4" data-testid={`special-row-${sp.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-3">
                                <h4 className="font-heading font-medium text-base sm:text-lg text-[#3D4035] leading-snug">
                                  {sp.display_name || sp.name}
                                </h4>
                                {sp.price != null && (
                                  <span className="font-heading font-semibold text-base sm:text-lg text-[#E07A5F] whitespace-nowrap">
                                    {sp.currency ? `${sp.currency} ` : ""}{sp.price}
                                  </span>
                                )}
                              </div>
                              {sp.display_description && (
                                <p className="mt-1 text-sm leading-snug text-[#6A6F5C]">
                                  {sp.display_description}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-3 text-[11px] text-[#9A9E8D] mt-2">
                                {sp.category && <span className="uppercase tracking-[0.16em] font-semibold">{translateCategory(sp.category, t)}</span>}
                                {sp.valid_days?.length > 0 && (
                                  <span className="inline-flex items-center gap-1"><Clock size={11} /> {translateDayCodes(sp.valid_days, t).join(", ")}</span>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
                );
              })}
            </AnimatePresence>
          </Accordion>
        )}
      </section>
    </div>
  );
}
